import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import {
  faArrowDownShortWide,
  faBars,
  faBoxArchive,
  faCheck,
  faFilter,
  faFlag,
  faGripLines,
  faHand,
  faMagnifyingGlass,
  faStamp,
  faTableColumns,
  faUserCheck
} from '@fortawesome/free-solid-svg-icons';
import { Input, Switch } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { toast } from 'sonner';
import CallDrawer from '../../components/CallDrawer';
import { useAuth } from '../../context/AuthContext';
import { useActions, useActionStep } from '../../queries/actions';
import { useAssistantName } from '../../queries/agents';
import { useMeQuery } from '../../queries/me';
import { queryKeys } from '../../queries/queryKeys';
import { assigneeName, useBookingAssigneesQuery } from '../../queries/scheduling';
import { useTeamQuery } from '../../queries/users';
import { portalApi } from '../../services/portalApi';
import { errorText } from '../../utils/errors';
import {
  ALL_TYPES,
  LANES,
  OPEN_LANES,
  TYPES,
  byUrgency,
  defaultWaits,
  isMine,
  liveLadder,
  outcomeLabel,
  roleLabel,
  stampOf
} from '../../utils/actions';
import { callerLabel } from '../../utils/callLog';
import NewBookingModal from '../Scheduling/NewBookingModal';
import TicketFormModal from '../Maintenance/TicketFormModal';
import ActionCenter from './ActionCenter';
import Card from './Card';
import FinishModal from './FinishModal';
import FlagDrawer from './FlagDrawer';
import { AaTheme, Btn, Chip, ColSort, House, I, PillSelect, Seg, cx } from './ui';
import { usePhone } from '../../hooks/usePhone';
import './aa.css';

const UNDO_MS = 10000;
const SORT_OPTS = [
  { value: 'urgent', label: 'Urgent first' },
  { value: 'newest', label: 'Newest' },
  { value: 'oldest', label: 'Oldest' }
];
const EMPTY = {
  needs: 'Nothing new. Maggie puts anything that needs a person here.',
  progress: 'Nobody is working a card. Cards move here when someone picks them up.',
  review: 'Nothing to review.',
  office: 'Nothing escalated. Move a card here when the owner or office manager needs to sign off.'
};

function useNow(fast) {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), fast ? 1000 : 30000);
    return () => clearInterval(t);
  }, [fast]);
  return now;
}

const read = (key, def) => {
  try {
    return localStorage.getItem(key) || def;
  } catch {
    return def;
  }
};
const write = (key, value) => {
  try {
    localStorage.setItem(key, value);
  } catch {}
};

export default function Todos() {
  const location = useLocation();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const phone = usePhone();
  const { user, can } = useAuth();
  const canManage = can('manage_tasks');
  const agent = useAssistantName();
  const { data: profile } = useMeQuery();
  const orgName = profile?.organization?.name || '';
  const teamRole = profile?.user?.teamRole ?? user?.teamRole ?? '';
  const me = { ...user, teamRole };
  const actor = roleLabel(teamRole) || (user?.role === 'owner' ? 'Owner' : user?.fullName || 'Staff');
  const { data: team = [] } = useBookingAssigneesQuery();
  const { data: users = [] } = useTeamQuery();

  const [mode, setMode] = useState(() => (phone ? 'approvals' : 'board'));
  const [type, setType] = useState('all');
  const [assignee, setAssignee] = useState(defaultWaits(teamRole).length ? 'me' : 'everyone');
  const [q, setQ] = useState('');
  const [sort, setSort] = useState('urgent');
  const [colSort, setColSort] = useState({});
  const pickSort = (v) => {
    setSort(v);
    setColSort({});
  };
  const [size, setSizeState] = useState(() => read('aaSize', 'standard'));
  const [showDone, setShowDone] = useState(false);
  const [phLane, setPhLane] = useState('needs');
  const [fOpen, setFOpen] = useState(false);
  const [openId, setOpenId] = useState(location.state?.openActionId || new URLSearchParams(location.search).get('task'));
  const [finishing, setFinishing] = useState(null);
  const [flagging, setFlagging] = useState(null);
  const [callId, setCallId] = useState(null);
  const [sched, setSched] = useState(null);
  const [party, setParty] = useState(false);
  const [pulse, setPulse] = useState(false);
  const [pending, setPending] = useState({});
  const timers = useRef({});
  const laneRef = useRef(null);
  const archRef = useRef(null);
  const step = useActionStep();

  const { rows, tz } = useActions('7');
  const now = useNow(rows.some(liveLadder));

  useEffect(() => {
    if (location.state?.openActionId) setOpenId(location.state.openActionId);
  }, [location.state]);

  useEffect(() => {
    if (!party) return undefined;
    const t = setTimeout(() => setParty(false), 3600);
    return () => clearTimeout(t);
  }, [party]);

  useEffect(() => {
    if (!pulse) return undefined;
    const t = setTimeout(() => setPulse(false), 2200);
    return () => clearTimeout(t);
  }, [pulse]);

  useEffect(() => {
    if (phone && showDone) archRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [phone, showDone]);

  const pickLane = (key) => {
    setPhLane(key);
    const el = laneRef.current;
    if (el && el.getBoundingClientRect().top < el.firstChild.getBoundingClientRect().top) el.scrollIntoView({ block: 'start' });
  };

  const setSize = (v) => {
    setSizeState(v);
    write('aaSize', v);
  };

  const owners = useMemo(() => Object.fromEntries(users.filter((p) => p.teamRole).map((p) => [p.teamRole, assigneeName(p)])), [users]);
  const ownerOf = (x) =>
    x.mine?.name
      ? x.mine.name
      : x.wait === '' || !x.wait
        ? 'Unassigned'
        : x.wait === 'agent_zero'
          ? 'Agent Zero team'
          : x.wait === 'agent'
            ? agent
            : owners[x.wait] || roleLabel(x.wait);

  const all = useMemo(
    () =>
      rows.map((x) =>
        pending[x._id]
          ? {
              ...x,
              lane: 'done',
              result: `${pending[x._id].edited ? 'Approved edited by' : 'Approved by'} ${actor}.`,
              resolvedBy: { role: actor },
              resolvedAt: pending[x._id].at
            }
          : x
      ),
    [rows, pending, actor]
  );

  const whoOk = (x) => {
    if (assignee === 'everyone') return true;
    if (assignee === 'me') return isMine(x, me);
    if (assignee === 'unassigned') return !x.wait && !x.mine?.userId;
    return ownerOf(x) === assignee;
  };
  const text = q.trim().toLowerCase();
  const { list, done, lanes, counts } = useMemo(() => {
    const rowsList = all.filter(
      (x) =>
        (type === 'all' || x.type === type) &&
        whoOk(x) &&
        (!text || `${x.title} ${x.contact?.name || ''} ${x.who || ''}`.toLowerCase().includes(text))
    );
    const cnt = {};
    all.forEach((x) => {
      if (x.lane === 'done' || !whoOk(x)) return;
      cnt.all = (cnt.all || 0) + 1;
      cnt[x.type] = (cnt[x.type] || 0) + 1;
    });
    const byDate = (dir) => (a, b) => dir * (new Date(a.arrivedAt) - new Date(b.arrivedAt));
    const cmpFor = (s) => (s === 'newest' ? byDate(-1) : s === 'oldest' ? byDate(1) : byUrgency);
    return {
      list: rowsList,
      done: rowsList.filter((x) => x.lane === 'done').sort((a, b) => new Date(b.resolvedAt || 0) - new Date(a.resolvedAt || 0)),
      lanes: OPEN_LANES.map((l) => [l, rowsList.filter((x) => x.lane === l.key).sort(cmpFor(colSort[l.key] || sort))]),
      counts: cnt
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [all, type, text, assignee, owners, agent, teamRole, user?._id, sort, colSort]);

  const canAct = (x) => {
    if (!canManage) return 'Read only. Ask the owner for access.';
    if (x.kind === 'dispatch' && x.approve?.label && ['leasing_agent', 'maintenance_coordinator'].includes(teamRole))
      return 'Only the on-call manager approves dispatch.';
    if (x.kind === 'showing' && x.approve?.label && teamRole === 'maintenance_coordinator') return 'The leasing team confirms showings.';
    if (x.kind === 'batch' && teamRole === 'leasing_agent') return 'Maintenance team only.';
    return null;
  };

  const clearPending = (id) =>
    setPending((p) => {
      const { [id]: _, ...rest } = p;
      return rest;
    });

  const approve = (x, edit, edited) => {
    const label = (x.approve?.label || 'Approved').replace(/^Approve: /, '');
    setPending((p) => ({ ...p, [x._id]: { edited, at: new Date().toISOString() } }));
    setPulse(true);
    timers.current[x._id] = setTimeout(() => {
      delete timers.current[x._id];
      portalApi
        .actionStep(x._id, 'approve', edited ? { edit } : {})
        .catch((err) => toast.error(errorText(err, 'Could not approve this action')))
        .finally(() => {
          clearPending(x._id);
          qc.invalidateQueries({ queryKey: queryKeys.actions.all });
        });
    }, UNDO_MS);
    toast(`${label.charAt(0).toUpperCase()}${label.slice(1)}.`, {
      duration: UNDO_MS,
      action: {
        label: 'Undo',
        onClick: () => {
          clearTimeout(timers.current[x._id]);
          delete timers.current[x._id];
          clearPending(x._id);
          toast('Undone. Nothing was sent.');
        }
      }
    });
  };

  const run = (x) => (name, payload, finishAfter, msg) =>
    step.mutate(
      { id: x._id, step: name, payload },
      {
        onSuccess: () => {
          if (msg) toast(msg);
          if (finishAfter) setFinishing({ x, via: 'button' });
        }
      }
    );

  const primary = (x) => {
    if (x.lane === 'review') return step.mutate({ id: x._id, step: 'right' }, { onSuccess: () => toast(`Done: ${x.title}.`) });
    if (x.kind === 'batch') return setOpenId(x._id);
    return approve(x, null, false);
  };

  const finish = (payload) => {
    const { x, via } = finishing;
    step.mutate(
      { id: x._id, step: 'done', payload },
      {
        onSuccess: () => {
          setFinishing(null);
          setOpenId(null);
          if (via === 'drag') setParty(true);
          else setPulse(true);
          toast(`${via === 'drag' ? 'House party! Filed under completed: ' : 'Done: '}${x.title}.`);
        }
      }
    );
  };

  const askDone = (x, via = 'button') => {
    const why = canAct(x);
    if (why) return toast(why);
    setFinishing({ x, via });
  };

  const dropOnLane = (lane) => (e) => {
    e.preventDefault();
    e.currentTarget.classList.remove('over');
    const x = all.find((r) => r._id === e.dataTransfer.getData('text/plain'));
    if (!x || x.lane === lane || x.lane === 'done') return;
    const why = canAct(x);
    if (why) return toast(why);
    step.mutate(
      { id: x._id, step: 'move', payload: { lane } },
      {
        onSuccess: () => toast(`Moved to ${LANES.find((l) => l.key === lane).label}: ${x.title}.`)
      }
    );
  };

  const card = (x) => (
    <Card
      key={x._id}
      x={x}
      size={size}
      tz={tz}
      now={now}
      agent={agent}
      owner={ownerOf(x)}
      why={canAct(x)}
      onOpen={(c) => setOpenId(c._id)}
      onPrimary={primary}
    />
  );

  const column = ([l, items]) => (
    <div
      key={l.key}
      className="aa2-col"
      onDragOver={(e) => {
        e.preventDefault();
        e.currentTarget.classList.add('over');
      }}
      onDragLeave={(e) => e.currentTarget.classList.remove('over')}
      onDrop={dropOnLane(l.key)}
    >
      <div className="aa2-colh">
        <span className="aa2-dot" style={{ background: l.color }} />
        <span className="b">{l.label}</span>
        <span className="aa2-cnt num">{items.length}</span>
        <ColSort
          aria-label={`Sort ${l.label}`}
          value={colSort[l.key] || sort}
          onChange={(v) => setColSort((c) => ({ ...c, [l.key]: v }))}
          options={SORT_OPTS}
        />
      </div>
      <div className="aa2-colb">
        {items.length ? (
          items.map(card)
        ) : (
          <div className="aa2-empty">
            <I icon={faHand} />
            <div>{EMPTY[l.key].replace('Maggie', agent)}</div>
          </div>
        )}
      </div>
    </div>
  );

  const openCount = (t) => counts[t] || 0;
  const typeTabs = (
    <div className="aa-types">
      <Seg items={[ALL_TYPES, ...TYPES].map(([key, label]) => [key, label, null, openCount(key)])} value={type} onChange={setType} label="Type" />
    </div>
  );
  const people = [...new Set(team.map(assigneeName).filter(Boolean))];
  const tools = (
    <div className="aa-tools">
      <PillSelect
        aria-label="Assignee"
        icon={faUserCheck}
        tip="Show only the cards waiting on this person or role"
        value={assignee}
        onChange={setAssignee}
        options={[
          { value: 'everyone', label: 'Everyone' },
          { value: 'me', label: 'Me' },
          { value: 'unassigned', label: 'Unassigned' },
          ...people.map((p) => ({ value: p, label: p })),
          { value: 'Agent Zero team', label: 'Agent Zero team' }
        ]}
      />
      <PillSelect
        aria-label="Sort"
        icon={faArrowDownShortWide}
        tip="Set the default sort for every column. Change a single column from its own menu."
        value={sort}
        onChange={pickSort}
        options={SORT_OPTS}
      />
      <Seg
        items={[
          ['standard', 'Standard', faGripLines, undefined, 'Bigger cards with the contact, time and the main action'],
          ['compact', 'Compact', faBars, undefined, 'Smaller cards with just the title, priority and owner, to see more at once']
        ]}
        value={size}
        onChange={setSize}
        label="Card size"
      />
    </div>
  );
  const search = (
    <Input
      className="aa-search"
      allowClear
      prefix={<FontAwesomeIcon icon={faMagnifyingGlass} style={{ color: '#8C8C8C' }} />}
      placeholder="Search cards by title or contact"
      aria-label="Search cards"
      value={q}
      onChange={(e) => setQ(e.target.value)}
    />
  );
  const modeSeg = (
    <Seg
      items={[
        ['board', 'Board', faTableColumns],
        ['approvals', 'Approvals', faStamp]
      ]}
      value={mode}
      onChange={setMode}
      label="View"
    />
  );
  const doneSwitch = (
    <label className="aa-switch">
      <Switch size="small" checked={showDone} onChange={setShowDone} aria-label="Show completed" />
      <span>Show completed</span>
    </label>
  );

  let head = (
    <div className="aa2-head">
      <div className="grow">
        <h1>Agent Actions</h1>
        <div className="m-sub">
          Cards {agent} and the system create when someone needs to look or act. Urgent cards sort first. Drag a card to move it, or drag it down to
          the house to finish it.
        </div>
      </div>
      <div className="aa-headr">
        {doneSwitch}
        {modeSeg}
      </div>
    </div>
  );
  let filters = (
    <>
      <div className="aa-filters">
        {typeTabs}
        {tools}
      </div>
      {search}
    </>
  );
  if (phone) {
    const nF = (type !== 'all' ? 1 : 0) + (assignee !== 'everyone' ? 1 : 0) + (q ? 1 : 0);
    head = (
      <div className="m-col gap8">
        <div className="aa2-phtop">
          {modeSeg}
          <div className="grow" />
          <Btn sm icon={faBoxArchive} kind={showDone ? 'primary' : null} aria-pressed={showDone} onClick={() => setShowDone(!showDone)}>
            {`Completed (${done.length})`}
          </Btn>
        </div>
        <div className="aa2-phtop">
          <Btn sm icon={faFilter} kind={nF ? 'primary' : null} onClick={() => setFOpen(!fOpen)}>
            {`Filters${nF ? ` (${nF})` : ''}`}
          </Btn>
          {nF > 0 && (
            <button
              type="button"
              className="aa-link sm"
              onClick={() => {
                setType('all');
                setAssignee('everyone');
                setQ('');
              }}
            >
              Clear
            </button>
          )}
        </div>
        {fOpen && (
          <div className="aa2-fsheet">
            {typeTabs}
            {tools}
            {search}
          </div>
        )}
      </div>
    );
    filters = null;
  }

  const approvals = list
    .filter((x) => (x.lane === 'done' ? /^Approved/.test(x.result || '') : Boolean(x.approve?.label)))
    .sort((a, b) => (a.lane === 'done') - (b.lane === 'done'));
  const approvalsView = (
    <section className={cx('m-card')} aria-label="Approvals">
      <div className="m-ch">
        <span className="m-bub">
          <I icon={faStamp} />
        </span>
        <div className="grow">
          <div className="m-ct">Approvals</div>
          <div className="m-sub">What needed a person's OK, and who gave it</div>
        </div>
      </div>
      {approvals.length ? (
        approvals.map((x) => (
          <div key={x._id} className={cx('aa2-aprow', size === 'compact' && 'cmp')}>
            <div className="grow">
              <div className="b sm">{x.title}</div>
              {size !== 'compact' && <div className="m-sub">{[x.property, callerLabel(x)].filter(Boolean).join(' · ')}</div>}
            </div>
            {x.lane !== 'done' ? (
              <>
                <Chip fam="warn">Pending</Chip>
                {canAct(x) ? null : (
                  <Btn kind="primary" sm onClick={() => primary(x)}>
                    {x.approve.label}
                  </Btn>
                )}
                <Btn sm onClick={() => setOpenId(x._id)}>
                  Details
                </Btn>
              </>
            ) : (
              <>
                <Chip fam="ok" icon={faCheck}>
                  Approved
                </Chip>
                <div className="m-sub">
                  Approved by {x.resolvedBy?.name || x.resolvedBy?.role}, {stampOf(x.resolvedAt, tz, now)}
                </div>
              </>
            )}
          </div>
        ))
      ) : (
        <div className="m-sub">Nothing waiting on an approval.</div>
      )}
    </section>
  );

  const board = phone ? (
    <div className="m-col gap8" ref={laneRef}>
      <div className="aa2-phl" role="tablist">
        {lanes.map(([l, items]) => (
          <button
            key={l.key}
            type="button"
            role="tab"
            aria-selected={phLane === l.key}
            className={phLane === l.key ? 'on' : ''}
            onClick={() => pickLane(l.key)}
          >
            <span className="m-row gap4">
              <span className="aa2-dot" style={{ background: l.color }} />
              <span className="aa2-cnt num">{items.length}</span>
            </span>
            <span className="lbl">{l.label}</span>
          </button>
        ))}
      </div>
      {(() => {
        const items = lanes.find(([l]) => l.key === phLane)[1];
        return items.length ? (
          items.map(card)
        ) : (
          <div className="aa2-empty">
            <I icon={faHand} />
            <div>Nothing here.</div>
          </div>
        );
      })()}
    </div>
  ) : (
    <div className="aa2-board">{lanes.map(column)}</div>
  );

  const house = (
    <div className="aa2-housewrap">
      <House
        party={party}
        done={done.length}
        onDrop={(id) => {
          const x = all.find((r) => r._id === id);
          if (x && x.lane !== 'done') askDone(x, 'drag');
        }}
      />
      <div className="aa2-housenote">
        <I icon={faBoxArchive} />
        <div>
          <div className="sm">Drop a card on the house to finish it. You add a short note, then it files under Completed.</div>
          <div className="m-sub">Completed cards are hidden. Use Show completed to see the archive.</div>
        </div>
      </div>
    </div>
  );

  const archive = (
    <div className="aa2-arch" ref={archRef}>
      <div className="aa2-archh">
        <I icon={faBoxArchive} />
        <span className="b">Completed ({done.length})</span>
        <span className="m-sub grow">Archived. Newest first.</span>
        <Btn sm onClick={() => setShowDone(false)}>
          Hide
        </Btn>
      </div>
      {done.map((x) => (
        <button key={x._id} type="button" className="aa2-arow" onClick={() => setOpenId(x._id)}>
          <span className="aa2-acheck">
            <I icon={faCheck} />
          </span>
          <div className="grow">
            <div className="sm b">{x.title}</div>
            <div className="m-sub">
              {x.result || ''}
              {callerLabel(x) ? ` · ${callerLabel(x)}` : ''}
            </div>
          </div>
          <div className="aa2-ameta">
            <div className="xs">
              {x.resolvedBy?.role
                ? `${x.resolvedBy.name || x.resolvedBy.role}, ${stampOf(x.resolvedAt, tz, now)}`
                : `${agent} · AI, ${stampOf(x.arrivedAt, tz, now)}`}
            </div>
            {x.outcome && <Chip fam={x.outcome === 'lost' || x.outcome === 'disqualified' ? 'neutral' : 'ok'}>{outcomeLabel(x.outcome)}</Chip>}
            {(x.wtr?.state === 'flagged' || x.esc === 'Flagged') && (
              <Chip fam="warn" icon={faFlag}>
                Flagged
              </Chip>
            )}
          </div>
        </button>
      ))}
    </div>
  );

  const opened = openId && all.find((x) => x._id === openId);
  const close = () => {
    setOpenId(null);
    if (location.state?.openActionId || location.search) navigate('/todos', { replace: true, state: null });
  };

  return (
    <>
      <AaTheme>
        <div className={cx('aa', phone && 'ph')}>
          <div className="m-page">
            {head}
            {mode === 'approvals' ? (
              approvalsView
            ) : (
              <>
                {filters}
                {pulse && (
                  <div className="m-pulsebar">
                    <I icon={faCheck} /> Moved to Completed ({done.length})
                  </div>
                )}
                {board}
                {house}
                {showDone && archive}
              </>
            )}
          </div>

          {opened && (
            <ActionCenter
              key={opened._id}
              x={opened}
              tz={tz}
              now={now}
              agent={agent}
              phone={phone}
              owners={owners}
              team={users}
              why={canAct(opened)}
              canManage={canManage}
              run={run(opened)}
              onPrimary={primary}
              onApproveEdited={(x, values) => {
                approve(x, values, true);
                close();
              }}
              onFinish={(x) => askDone(x)}
              onFlag={setFlagging}
              onCall={setCallId}
              onSchedule={(kind, initial) => setSched({ kind, initial })}
              onClose={close}
            />
          )}
          {finishing && <FinishModal x={finishing.x} phone={phone} busy={step.isPending} onDone={finish} onClose={() => setFinishing(null)} />}
        </div>
      </AaTheme>
      {flagging && <FlagDrawer subject={flagging} agent={agent} orgName={orgName} onClose={() => setFlagging(null)} />}
      <CallDrawer callId={callId} onClose={() => setCallId(null)} />
      {sched?.kind === 'tour' && <NewBookingModal type="tour" assignees={team} tz={tz} initial={sched.initial} onClose={() => setSched(null)} />}
      {sched?.kind === 'maintenance' && <TicketFormModal initial={sched.initial} onClose={() => setSched(null)} />}
    </>
  );
}
